import React, { useRef, useState, useEffect } from 'react';
import cowSvg from './beefCutsCowSvg';
import { REGION_PRIMALS, resolveRegion } from './primalRegions';

// Normalize a group id to a primal slug: "shank_1" -> "shank".
const idToSlug = (id) => (id || '').replace(/_\d+$/, '').toLowerCase();

export default function PrimalCutsGraphic({ onSelect, primalCuts = [], highlightedPrimalIds = [] }) {
  const wrapperRef = useRef();
  const [hovered, setHovered] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState({ x: 0, y: 0 });

  // Resolve the region group under the event target to its matched primal(s).
  const regionFromEvent = (event) => {
    const target = event.target;
    const group = target && target.closest ? target.closest('g[id]') : null;
    if (!group) return null;
    const slug = idToSlug(group.getAttribute('id'));
    const resolved = resolveRegion(slug, primalCuts);
    return resolved.matched.length ? resolved : null;
  };

  // Reflect the selected primal on the diagram (persistent black fill) by
  // toggling `is-selected` on the matching region group(s). A region counts as
  // selected if any of the primals it covers are selected (loin ⇢ loin+sirloin).
  useEffect(() => {
    if (!wrapperRef.current) return;
    const selectedNames = primalCuts
      .filter((item) => item.selected)
      .map((item) => item.name.toLowerCase());

    wrapperRef.current.querySelectorAll('g[id]').forEach((group) => {
      const slug = idToSlug(group.getAttribute('id'));
      const config = REGION_PRIMALS[slug] || { slugs: [slug] };
      const isSelected = config.slugs.some((s) => selectedNames.includes(s));
      const matchesSearch = resolveRegion(slug, primalCuts).matched.some(
        (primal) => highlightedPrimalIds.includes(Number(primal.id))
      );
      group.classList.toggle('is-selected', isSelected);
      group.classList.toggle('is-search-match', matchesSearch);
    });
  }, [primalCuts, highlightedPrimalIds]);

  const handleClick = (event) => {
    const resolved = regionFromEvent(event);
    if (!resolved) return;

    const selectedIds = resolved.matched.map((primal) => primal.id);
    onSelect(selectedIds);

    const results = document.getElementById('cuts-results');
    if (results) results.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMove = (event) => {
    const resolved = regionFromEvent(event);
    if (!resolved) {
      setHovered(null);
      return;
    }

    const count = resolved.matched.reduce(
      (sum, primal) => sum + (primal.count || 0),
      0
    );
    const popularCuts = resolved.matched.flatMap(
      (primal) => primal.popular_cuts || []
    );

    const rect = wrapperRef.current.getBoundingClientRect();
    setFlyoutPos({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    });
    setHovered({ name: resolved.label, count, popular_cuts: popularCuts });
  };

  return (
    <div className="primal-cuts__graphic-wrapper" ref={wrapperRef}>
      <div
        className="primal-cuts__graphic"
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={() => setHovered(null)}
        dangerouslySetInnerHTML={{ __html: cowSvg }}
      />

      {hovered && (
        <div
          className="primal-cuts__flyout"
          style={{ left: flyoutPos.x, top: flyoutPos.y }}
        >
          <h4 className="primal-cuts__flyout-title">{hovered.name}</h4>
          {typeof hovered.count === 'number' && (
            <p className="primal-cuts__flyout-count">{hovered.count} Cuts</p>
          )}
          {hovered.popular_cuts && hovered.popular_cuts.length > 0 && (
            <>
              <p className="primal-cuts__flyout-label">Popular Cuts</p>
              <ul className="primal-cuts__flyout-list">
                {hovered.popular_cuts.map((cut) => (
                  <li key={cut.id}>{cut.name}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
